import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { apiService } from '../services/apiService';
import TalentBillingChart from './TalentBillingChart';
import AttachmentPreviewModal from './AttachmentPreviewModal';
import ClientSelectionFields from './ClientSelectionFields';
import { useFilePreview } from '../hooks/useFilePreview';
import { getCurrentResidenceStatusFromEntries, isSpecifiedSkill2Status, sortRenewalEntriesNewestFirst } from '../utils/residenceStatus';
import { RESIDENCE_STATUS_OPTIONS } from '../config/residenceStatusConfig';

const DetailItem = ({ label, value }) => (
    <div className="grid grid-cols-3 gap-4 py-2 border-b border-gray-100">
        <dt className="text-sm font-medium text-gray-500">{label}</dt>
        <dd className="mt-1 text-sm text-gray-900 col-span-2 sm:mt-0">{value || 'N/A'}</dd>
    </div>
);

const EditItem = ({ label, name, value, onChange, type = 'text', inputMode, pattern, maxLength, disabled = false }) => {
    // Set date range limits for date inputs to restrict year to 4 digits
    const dateProps = type === 'date' ? {
        min: '1900-01-01',
        max: '2099-12-31'
    } : {};

    return (
        <div className="grid grid-cols-3 gap-4 py-2 border-b border-gray-100">
            <label htmlFor={name} className="text-sm font-medium text-gray-700 flex items-center">{label}</label>
            <div className="col-span-2">
                <input
                    type={type}
                    name={name}
                    id={name}
                    value={value || ''}
                    onChange={onChange}
                    inputMode={inputMode}
                    pattern={pattern}
                    maxLength={maxLength}
                    disabled={disabled}
                    {...dateProps}
                    className={`block w-full border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 ${disabled ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}`}
                />
            </div>
        </div>
    );
};

const SelectItem = ({ label, name, value, onChange, onInput, required = false, options, optionType, t, getTranslatedOption, disabled = false }) => (
    <div className="grid grid-cols-3 gap-4 py-2 border-b border-gray-100">
        <label htmlFor={name} className="text-sm font-medium text-gray-700 flex items-center">
            {label}{required && '*'}
        </label>
        <div className="col-span-2">
            <select
                name={name}
                id={name}
                value={value || ''}
                onChange={onChange}
                onInput={onInput}
                required={required}
                disabled={disabled}
                className={`block w-full border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 ${disabled ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}`}
            >
                <option value="" disabled>{t('pleaseSelect')}</option>
                {options.map(option => (
                    <option key={option} value={option}>
                        {getTranslatedOption ? getTranslatedOption(option, optionType) : option}
                    </option>
                ))}
            </select>
        </div>
    </div>
);

const SectionTitle = ({ title }) => (
    <div className="pt-4 mt-4 border-t border-gray-200">
        <h4 className="text-md font-semibold text-gray-700">{title}</h4>
    </div>
);

const isImage = (fileType) => fileType.startsWith('image/');

const renewalApplicationTypeOptions = ["期間更新", "認定証明書申請", "資格変更"];
const renewalSubmissionMethodOptions = ["対面提出", "オンライン提出"];
const renewalSubmissionAgentOptions = ["スティーブン", "ナン", "ガン", "他"];
const submissionAgentFixedOptions = ["スティーブン", "ナン", "ガン"];

const getSubmissionAgentOptionValue = (value) => {
    const trimmed = (value || '').trim();
    if (!trimmed) return '';
    if (submissionAgentFixedOptions.includes(trimmed)) return trimmed;
    return '他';
};

const normalizePauseDate = (value) => {
    if (!value) return '';
    if (typeof value === 'string') {
        return value.split('T')[0];
    }
    return value;
};

const mapPauseRowsFromApi = (rows, talentSnapshot) => {
    const mapped = (Array.isArray(rows) ? rows : []).map((row, index) => ({
        id: row.id || `pause-existing-${index}`,
        pauseType: row.pauseType || "育休",
        startDate: normalizePauseDate(row.startDate),
        endDate: normalizePauseDate(row.endDate)
    }));

    if (mapped.length > 0) {
        return mapped;
    }

    const snapshotType = (talentSnapshot?.employmentPauseType || '').trim();
    if ((snapshotType === '育休' || snapshotType === '一時帰国') && talentSnapshot?.employmentPauseStartDate) {
        return [{
            id: 'pause-legacy',
            pauseType: snapshotType,
            startDate: normalizePauseDate(talentSnapshot.employmentPauseStartDate),
            endDate: normalizePauseDate(talentSnapshot.employmentPauseEndDate)
        }];
    }

    return [];
};

export default function AdminTalentDetailModal({ talentId, isOpen, onClose }) {
    const { t } = useTranslation();
    const attachmentPreview = useFilePreview(apiService.downloadAttachment);
    const [talent, setTalent] = useState(null);
    const [talentFormData, setTalentFormData] = useState({});
    const [employmentPauseHistory, setEmploymentPauseHistory] = useState([]);
    const [residenceFormData, setResidenceFormData] = useState({});
    const [allAttachments, setAllAttachments] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [activeTab, setActiveTab] = useState('basic');
    const [billingChartRefreshKey, setBillingChartRefreshKey] = useState(0);

    // Fee Change state
    const [feeChanges, setFeeChanges] = useState([]);
    const [feeChangeLoading, setFeeChangeLoading] = useState(false);
    const [feeChangeFormOpen, setFeeChangeFormOpen] = useState(false);
    const [feeChangeForm, setFeeChangeForm] = useState({ effectiveMonth: '', newFee: '', note: '' });
    const [feeChangeSubmitting, setFeeChangeSubmitting] = useState(false);
    const [baseFeeEditUnlocked, setBaseFeeEditUnlocked] = useState(false);
    const [thumbnailUrls, setThumbnailUrls] = useState({});
    const [customFieldValue, setCustomFieldValue] = useState('');
    const [showCustomField, setShowCustomField] = useState(false);

    // Photo-specific state
    const [existingPhoto, setExistingPhoto] = useState(null);
    const [photoPreviewUrl, setPhotoPreviewUrl] = useState(null);

    // Renewal entries state for residence tab
    const [renewalEntries, setRenewalEntries] = useState([]);
    const requestRef = useRef(0);
    const modalRef = useRef(null);
    const dragRef = useRef({
        dragging: false,
        startX: 0,
        startY: 0,
        originX: 0,
        originY: 0
    });
    const [isDesktopModal, setIsDesktopModal] = useState(() => (
        typeof window !== 'undefined' ? window.innerWidth >= 1024 : false
    ));
    const [modalPosition, setModalPosition] = useState(null);

    // Options for dropdowns
    const genderOptions = useMemo(() => ["男", "女"], []);
    const nationalityOptions = useMemo(() => ["中国", "韩国", "缅甸", "印度尼西亚", "越南", "菲律宾"], []);
    const fieldOptions = useMemo(() => ["外食", "飲食料品製造", "溶接", "介護", "他"], []);
    const employmentStatusOptions = useMemo(() => ["内定", "申請中", "在職", "離職", "待入国"], []);
    const billingPlanTypeOptions = useMemo(() => ["通常", "初期費+Nヶ月管理費", "段階管理費"], []);
    const pauseTypeOptions = useMemo(() => ["育休", "一時帰国"], []);
    const createPauseItem = () => ({
        id: `pause-${Date.now()}-${Math.random()}`,
        pauseType: "育休",
        startDate: '',
        endDate: ''
    });

    const sanitizePauseRows = (rows) => {
        return (Array.isArray(rows) ? rows : [])
            .map(row => ({
                pauseType: (row.pauseType || '').trim(),
                startDate: normalizePauseDate(row.startDate),
                endDate: normalizePauseDate(row.endDate) || null
            }))
            .filter(row => (row.pauseType === '育休' || row.pauseType === '一時帰国') && row.startDate);
    };

    const resolveCurrentPauseSnapshot = (rows, employmentStatus) => {
        if (employmentStatus !== '在職') {
            return { pauseType: 'なし', startDate: null, endDate: null };
        }

        const today = new Date().toISOString().slice(0, 10);
        const activeRow = rows
            .filter(row => row.startDate && row.startDate <= today)
            .filter(row => !row.endDate || row.endDate >= today)
            .sort((a, b) => (a.startDate < b.startDate ? 1 : -1))[0];

        if (!activeRow) {
            return { pauseType: 'なし', startDate: null, endDate: null };
        }

        return {
            pauseType: activeRow.pauseType,
            startDate: activeRow.startDate,
            endDate: activeRow.endDate || null
        };
    };

    // Translation mapping for display
    const getTranslatedOption = (value, type) => {
        const translations = {
            gender: { "男": t('male'), "女": t('female') },
            nationality: {
                "中国": t('chinese'),
                "韩国": t('korean'),
                "韓国": t('korean'),
                "缅甸": t('myanmar'),
                "ミャンマー": t('myanmar'),
                "印度尼西亚": t('indonesia'),
                "インドネシア": t('indonesia'),
                "越南": t('vietnamese'),
                "ベトナム": t('vietnamese'),
                "台湾": t('taiwan'),
                "日本": t('japanese'),
                "菲律宾": t('filipino'),
                "フィリピン": t('filipino'),
                "泰国": t('thailand')
            },
            field: {
                "外食": t('restaurant'),
                "飲食料品製造": t('foodManufacturing'),
                "溶接": t('welding'),
                "介護": t('care'),
                "他": t('otherField')
            },
            employmentStatus: {
                "内定": t('jobOffer'),
                "申請中": t('applying'),
                "在職": t('employed'),
                "離職": t('unemployed'),
                "待入国": t('awaitingEntry')
            },
            employmentPauseType: {
                "なし": t('employmentPauseTypeNone'),
                "育休": t('employmentPauseTypeChildcare'),
                "一時帰国": t('employmentPauseTypeTemporaryReturn')
            },
            billingPlanType: {
                "通常": t('billingPlanTypeNormal'),
                "初期費+Nヶ月管理費": t('billingPlanTypeInitialPlus'),
                "段階管理費": t('billingPlanTypeTiered')
            }
        };
        return translations[type]?.[value] || value;
    };

    const fetchTalentDetails = useCallback(async () => {
        if (!talentId) return;
        const requestId = ++requestRef.current;
        setIsLoading(true);
        setError('');
        try {
            console.log(`[AdminTalentDetailModal] Fetching details for talent: ${talentId}`);

            const [talentData, attachmentsData, pauseData] = await Promise.all([
                apiService.getTalentById(talentId),
                apiService.getAttachmentsForTalent(talentId),
                apiService.getTalentEmploymentPauses(talentId).catch(() => ({ pauses: [] }))
            ]);

            console.log(`[AdminTalentDetailModal] Received talent data:`, talentData?.fullName);
            console.log(`[AdminTalentDetailModal] Received ${attachmentsData?.length || 0} attachments`);

            if (requestRef.current !== requestId) {
                return;
            }

            let loanData = talentData?.loan;
            // 补充一次直接获取的借款信息，防止主接口未返回贷款数据
            try {
                const directLoan = await apiService.getLoanByTalentId(talentId);
                loanData = directLoan || loanData;
            } catch (loanErr) {
                console.warn('[AdminTalentDetailModal] Loan fetch fallback failed:', loanErr?.message);
            }

            const mergedTalent = loanData ? { ...talentData, loan: loanData } : talentData;

            setTalent(mergedTalent);
            setTalentFormData(mergedTalent ? {
                ...mergedTalent,
                billingPlanType: mergedTalent.billingPlanType || "通常",
                employmentPauseType: mergedTalent.employmentPauseType || "なし",
                initialCostNotCharged: Boolean(mergedTalent.initialCostNotCharged),
                managementFeeNotCharged: Boolean(mergedTalent.managementFeeNotCharged)
            } : {});
            setEmploymentPauseHistory(mapPauseRowsFromApi(pauseData?.pauses, mergedTalent));
            setAllAttachments(attachmentsData);

            // Initialize residence form data
            if (talentData?.residencePermit) {
                setResidenceFormData(talentData.residencePermit);

                // Initialize renewal entries from residence data
                if (talentData.residencePermit.renewalEntries && Array.isArray(talentData.residencePermit.renewalEntries)) {
                    setRenewalEntries(sortRenewalEntriesNewestFirst(talentData.residencePermit.renewalEntries).map(entry => ({
                        id: entry.id || Date.now() + Math.random(),
                        renewalDate: entry.renewalDate || '',
                        renewalGrantDate: entry.renewalGrantDate || '',
                        renewalNumber: entry.renewalNumber || '',
                        residenceStatus: entry.residenceStatus || '',
                        renewalApplicationType: entry.renewalApplicationType || '',
                        submissionMethod: entry.submissionMethod || '',
                        submissionAgent: entry.submissionAgent || '',
                        createdAt: entry.createdAt || '',
                        submissionAgentOption: getSubmissionAgentOptionValue(entry.submissionAgent),
                        submissionAgentOther: submissionAgentFixedOptions.includes(entry.submissionAgent || '') ? '' : (entry.submissionAgent || '')
                    })));
                } else {
                    setRenewalEntries([]);
                }
            } else {
                setResidenceFormData({});
                setRenewalEntries([]);
            }

            // Check if field value is custom (not in standard options)
            const standardFields = ["外食", "飲食料品製造", "溶接", "介護", "他"];
            if (talentData?.field && !standardFields.includes(talentData.field)) {
                console.log('[AdminTalentDetailModal] Detected custom field value:', talentData.field);
                setShowCustomField(true);
                setCustomFieldValue(talentData.field);
            } else {
                setShowCustomField(false);
                setCustomFieldValue('');
            }

            // Handle existing photo (with error tolerance)
            const photoAttachments = attachmentsData.filter(att => att.sourceModule === "Photo");
            if (photoAttachments.length > 0) {
                const photo = photoAttachments[0]; // Use first photo
                setExistingPhoto(photo);
                // Load photo preview
                try {
                    console.log(`[AdminTalentDetailModal] Loading photo: ${photo.fileName}`);
                    const photoBlob = await apiService.downloadAttachment(photo.id);
                    if (requestRef.current !== requestId) {
                        return;
                    }
                    const photoUrl = URL.createObjectURL(photoBlob);
                    setPhotoPreviewUrl(prevUrl => {
                        if (prevUrl) {
                            URL.revokeObjectURL(prevUrl);
                        }
                        return photoUrl;
                    });
                } catch (photoErr) {
                    console.warn(`[AdminTalentDetailModal] Failed to load photo ${photo.fileName}:`, photoErr.message);
                    // 照片加载失败不影响整体显示
                    setPhotoPreviewUrl(null);
                }
            } else {
                if (requestRef.current !== requestId) {
                    return;
                }
                setExistingPhoto(null);
                setPhotoPreviewUrl(null);
            }

            // Generate thumbnails for all images (with error tolerance)
            const newThumbnailUrls = {};
            for (const att of attachmentsData) {
                if (isImage(att.fileType)) {
                    try {
                        console.log(`[AdminTalentDetailModal] Loading thumbnail: ${att.fileName}`);
                        const blob = await apiService.downloadAttachment(att.id);
                        if (requestRef.current !== requestId) {
                            return;
                        }
                        newThumbnailUrls[att.id] = URL.createObjectURL(blob);
                    } catch (thumbErr) {
                        console.warn(`[AdminTalentDetailModal] Failed to load thumbnail ${att.fileName}:`, thumbErr.message);
                        // 缩略图加载失败不影响其他图片
                    }
                }
            }
            if (requestRef.current !== requestId) {
                Object.values(newThumbnailUrls).forEach(URL.revokeObjectURL);
                return;
            }
            setThumbnailUrls(newThumbnailUrls);

            console.log(`[AdminTalentDetailModal] Successfully loaded all data`);

        } catch (err) {
            console.error(`[AdminTalentDetailModal] ERROR fetching talent details:`, err);
            console.error(`[AdminTalentDetailModal] Error type: ${err.name}`);
            console.error(`[AdminTalentDetailModal] Error message: ${err.message}`);
            if (err.stack) {
                console.error(`[AdminTalentDetailModal] Stack trace:`, err.stack);
            }
            setError(err.message || t('fetchFailed'));
        } finally {
            if (requestRef.current !== requestId) {
                return;
            }
            setIsLoading(false);
        }
    }, [talentId, t]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        // Reset photo state when opening modal for different talent
        setExistingPhoto(null);
        setPhotoPreviewUrl(prevUrl => {
            if (prevUrl) {
                URL.revokeObjectURL(prevUrl);
            }
            return null;
        });
        setThumbnailUrls(prev => {
            Object.values(prev).forEach(URL.revokeObjectURL);
            return {};
        });
        setEmploymentPauseHistory([]);
        setBaseFeeEditUnlocked(false);

        fetchTalentDetails();

        // Load fee changes (with race-condition guard)
        if (talentId) {
            const requestedId = talentId;
            setFeeChangeLoading(true);
            apiService.getFeeChanges(requestedId)
                .then(res => {
                    // Only apply if we're still viewing the same talent
                    if (requestedId === talentId) {
                        setFeeChanges(res.changes || []);
                    }
                })
                .catch(() => {
                    if (requestedId === talentId) {
                        setFeeChanges([]);
                    }
                })
                .finally(() => {
                    if (requestedId === talentId) {
                        setFeeChangeLoading(false);
                    }
                });
        }
    }, [isOpen, fetchTalentDetails, talentId]);

    // Cleanup thumbnails and photo preview when component unmounts or URLs change
    useEffect(() => {
        return () => {
            Object.values(thumbnailUrls).forEach(URL.revokeObjectURL);
            if (photoPreviewUrl) {
                URL.revokeObjectURL(photoPreviewUrl);
            }
        };
    }, [thumbnailUrls, photoPreviewUrl]);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }

        const updateModalMode = () => {
            const desktop = window.innerWidth >= 1024;
            setIsDesktopModal(desktop);
            if (!desktop) {
                setModalPosition(null);
            }
        };

        updateModalMode();
        window.addEventListener('resize', updateModalMode);
        return () => window.removeEventListener('resize', updateModalMode);
    }, []);

    useEffect(() => {
        if (isOpen) {
            setModalPosition(null);
        } else {
            dragRef.current.dragging = false;
            document.body.style.userSelect = '';
            document.body.style.cursor = '';
        }
    }, [isOpen]);

    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!dragRef.current.dragging || !modalRef.current) {
                return;
            }

            const deltaX = e.clientX - dragRef.current.startX;
            const deltaY = e.clientY - dragRef.current.startY;
            const modalWidth = modalRef.current.offsetWidth;
            const modalHeight = modalRef.current.offsetHeight;

            const maxX = Math.max(8, window.innerWidth - modalWidth - 8);
            const maxY = Math.max(8, window.innerHeight - modalHeight - 8);

            const nextX = Math.min(Math.max(8, dragRef.current.originX + deltaX), maxX);
            const nextY = Math.min(Math.max(8, dragRef.current.originY + deltaY), maxY);
            setModalPosition({ x: nextX, y: nextY });
        };

        const handleMouseUp = () => {
            if (!dragRef.current.dragging) {
                return;
            }
            dragRef.current.dragging = false;
            document.body.style.userSelect = '';
            document.body.style.cursor = '';
        };

        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
            document.body.style.userSelect = '';
            document.body.style.cursor = '';
        };
    }, []);

    const handleModalDragStart = useCallback((e) => {
        if (!isDesktopModal || e.button !== 0 || !modalRef.current) {
            return;
        }

        const rect = modalRef.current.getBoundingClientRect();
        dragRef.current = {
            dragging: true,
            startX: e.clientX,
            startY: e.clientY,
            originX: rect.left,
            originY: rect.top
        };

        setModalPosition({ x: rect.left, y: rect.top });
        document.body.style.userSelect = 'none';
        document.body.style.cursor = 'move';
    }, [isDesktopModal]);

    const handleDownload = async (attachmentId, fileName) => {
        try {
            console.log(`[AdminTalentDetailModal] Downloading attachment: ${fileName} (${attachmentId})`);
            const blob = await apiService.downloadAttachment(attachmentId);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url); // Clean up the object URL
            console.log(`[AdminTalentDetailModal] Download completed: ${fileName}`);
        } catch (err) {
            console.error(`[AdminTalentDetailModal] Download failed for ${fileName}:`, err);
            alert(`${t('downloadFailed')}: ${fileName}\n${err.message}`);
        }
    };

    // Photo handlers
    const handlePhotoChange = async (e) => {
        const file = e.target.files[0];
        if (file) {
            // Validate file type
            if (!file.type.startsWith('image/')) {
                setError(t('onlyImagesAllowed') || '画像ファイルのみアップロード可能です');
                return;
            }

            // Validate file size (e.g., max 5MB)
            if (file.size > 5 * 1024 * 1024) {
                setError(t('fileTooLarge') || 'ファイルサイズが大きすぎます（最大5MB）');
                return;
            }

            // Create preview URL
            const url = URL.createObjectURL(file);
            if (photoPreviewUrl) {
                URL.revokeObjectURL(photoPreviewUrl);
            }
            setPhotoPreviewUrl(url);

            // Upload immediately
            try {
                setError('');
                // Delete existing photo first if exists
                if (existingPhoto) {
                    await apiService.deleteAttachment(existingPhoto.id);
                }
                // Upload new photo with "Photo" module type
                await apiService.uploadAttachment(talentId, file, "Photo");
                // Refresh data to get the new photo
                fetchTalentDetails();
            } catch (err) {
                setError(err.message || t('uploadFailed'));
                if (photoPreviewUrl) {
                    URL.revokeObjectURL(photoPreviewUrl);
                }
                setPhotoPreviewUrl(null);
            }
        }
    };

    const handleDownloadPhoto = async () => {
        if (existingPhoto) {
            try {
                const blob = await apiService.downloadAttachment(existingPhoto.id);
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = existingPhoto.fileName || 'photo.jpg';
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
            } catch (err) {
                setError(t('downloadFailed') + ': ' + err.message);
            }
        }
    };

    const handleDeletePhoto = async () => {
        if (existingPhoto && window.confirm(t('confirmDelete'))) {
            try {
                await apiService.deleteAttachment(existingPhoto.id);
                setExistingPhoto(null);
                if (photoPreviewUrl) {
                    URL.revokeObjectURL(photoPreviewUrl);
                }
                setPhotoPreviewUrl(null);
                // Refresh data
                fetchTalentDetails();
            } catch (err) {
                setError(t('deleteFailed') + ': ' + err.message);
            }
        }
    };

    // Form change handlers
    const handleTalentChange = (e) => {
        const { name, value, type, checked } = e.target;
        const resolvedValue = type === 'checkbox' ? checked : value;
        console.log('[AdminTalentDetailModal] handleTalentChange:', name, '=', value);
        if (name === 'initialCostNotCharged') {
            const nextData = { ...talentFormData, initialCostNotCharged: Boolean(resolvedValue) };
            if (nextData.initialCostNotCharged) {
                nextData.initialCost = '';
            }
            setTalentFormData(nextData);
            return;
        }
        if (name === 'managementFeeNotCharged') {
            const nextData = { ...talentFormData, managementFeeNotCharged: Boolean(resolvedValue) };
            if (nextData.managementFeeNotCharged) {
                nextData.managementFee = 0;
                nextData.firstMonthManagementFee = '';
                nextData.includedManagementMonths = '';
                nextData.tier1Months = '';
                nextData.tier1ManagementFee = '';
                nextData.tier2Months = '';
                nextData.tier2ManagementFee = '';
                nextData.billingPlanType = '通常';
                setBaseFeeEditUnlocked(false);
            }
            setTalentFormData(nextData);
            return;
        }
        if (name === 'employmentStatus') {
            const nextData = { ...talentFormData, employmentStatus: value };
            if (value !== '在職') {
                nextData.employmentPauseType = 'なし';
                nextData.employmentPauseStartDate = '';
                nextData.employmentPauseEndDate = '';
            }
            setTalentFormData(nextData);
            return;
        }
        if (name === 'billingPlanType') {
            if (talentFormData.managementFeeNotCharged) {
                return;
            }
            const nextData = { ...talentFormData, billingPlanType: value };
            if (value === '通常') {
                nextData.includedManagementMonths = '';
                nextData.tier1Months = '';
                nextData.tier1ManagementFee = '';
                nextData.tier2Months = '';
                nextData.tier2ManagementFee = '';
            } else if (value === '初期費+Nヶ月管理費') {
                nextData.tier1Months = '';
                nextData.tier1ManagementFee = '';
                nextData.tier2Months = '';
                nextData.tier2ManagementFee = '';
            } else if (value === '段階管理費') {
                nextData.includedManagementMonths = '';
            }
            if (value !== '通常') {
                setBaseFeeEditUnlocked(false);
            }
            setTalentFormData(nextData);
            return;
        }
        if (name === 'managementNumber') {
            const numericValue = (value || '').replace(/\D/g, '');
            setTalentFormData({ ...talentFormData, [name]: numericValue });
            return;
        }
        if (name === 'field') {
            if (value === '他') {
                console.log('[AdminTalentDetailModal] Setting showCustomField=true');
                setShowCustomField(true);
                setCustomFieldValue('');
                setTalentFormData({ ...talentFormData, [name]: value });
            } else {
                console.log('[AdminTalentDetailModal] Setting showCustomField=false, value=', value);
                setShowCustomField(false);
                setCustomFieldValue('');
                setTalentFormData({ ...talentFormData, [name]: value });
            }
        } else {
            setTalentFormData({ ...talentFormData, [name]: value });
        }
    };

    const handlePauseRowChange = (id, field, value) => {
        setEmploymentPauseHistory(prev => prev.map(row => (
            row.id === id ? { ...row, [field]: value } : row
        )));
    };

    const handleAddPauseRow = () => {
        setEmploymentPauseHistory(prev => [...prev, createPauseItem()]);
    };

    const handleRemovePauseRow = (id) => {
        setEmploymentPauseHistory(prev => prev.filter(row => row.id !== id));
    };

    const handleCustomFieldChange = (e) => {
        const value = e.target.value;
        setCustomFieldValue(value);
        setTalentFormData({ ...talentFormData, field: value || '' });
    };

    const handleClientSelectionChange = (patch) => {
        setTalentFormData(prev => ({
            ...prev,
            ...patch
        }));
    };

    const handleResidenceChange = (e) => {
        const { name, value } = e.target;
        setResidenceFormData({ ...residenceFormData, [name]: value });
    };


    // Renewal entries handlers
    const handleAddRenewal = () => {
        const newEntry = {
            id: Date.now(), // Simple unique ID
            renewalDate: '',
            renewalGrantDate: '',
            renewalNumber: '',
            residenceStatus: '',
            renewalApplicationType: '',
            submissionMethod: '',
            submissionAgent: '',
            submissionAgentOption: '',
            submissionAgentOther: ''
        };
        setRenewalEntries(prev => [newEntry, ...prev]);
    };

    const handleRenewalChange = (id, field, value) => {
        setRenewalEntries(renewalEntries.map(entry =>
            entry.id === id ? { ...entry, [field]: value } : entry
        ));
    };

    const handleSubmissionAgentOptionChange = (id, value) => {
        setRenewalEntries(renewalEntries.map(entry => {
            if (entry.id !== id) return entry;
            if (value === '他') {
                const otherValue = entry.submissionAgentOther || '';
                return {
                    ...entry,
                    submissionAgentOption: '他',
                    submissionAgent: otherValue
                };
            }
            return {
                ...entry,
                submissionAgentOption: value,
                submissionAgentOther: '',
                submissionAgent: value
            };
        }));
    };

    const handleSubmissionAgentOtherChange = (id, value) => {
        setRenewalEntries(renewalEntries.map(entry =>
            entry.id === id ? {
                ...entry,
                submissionAgentOption: '他',
                submissionAgentOther: value,
                submissionAgent: value
            } : entry
        ));
    };

    const handleRemoveRenewal = (id) => {
        setRenewalEntries(renewalEntries.filter(entry => entry.id !== id));
    };

    const handleSaveResidence = async () => {
        setIsLoading(true);
        setError('');
        try {
            // Helper function to normalize date format to YYYY-MM-DD
            const normalizeDate = (dateValue) => {
                if (!dateValue) return null;
                if (typeof dateValue === 'string') {
                    // Extract YYYY-MM-DD format regardless of original format
                    const cleanDate = dateValue.split('T')[0];
                    return cleanDate && cleanDate.trim() !== '' ? cleanDate : null;
                }
                return null;
            };

            // Clean data similar to handleSaveTalent
            const cleanedData = { ...residenceFormData };

            // Remove navigation properties and non-data fields
            delete cleanedData.talent;
            delete cleanedData.residenceStaff;
            delete cleanedData.remainingDays;

            // Normalize all date fields to YYYY-MM-DD format
            const dateFields = ['applicationDate', 'grantDate', 'residenceCardExpiry', 'passportExpiry',
                'interviewDate', 'startDate', 'endDate', 'renewalDate', 'renewalGrantDate'];
            dateFields.forEach(field => {
                cleanedData[field] = normalizeDate(cleanedData[field]);
            });

            // Clean renewal entries - normalize dates and handle empty values
            const cleanRenewalEntries = renewalEntries.map(entry => ({
                renewalDate: normalizeDate(entry.renewalDate),
                renewalGrantDate: normalizeDate(entry.renewalGrantDate),
                renewalNumber: entry.renewalNumber || '',
                residenceStatus: entry.residenceStatus || '',
                renewalApplicationType: entry.renewalApplicationType || '',
                submissionMethod: entry.submissionMethod || '',
                submissionAgent: entry.submissionAgent || ''
            }));

            // Include renewal entries in save data
            cleanedData.renewalEntries = cleanRenewalEntries;

            await apiService.createOrUpdateResidence(talentId, cleanedData);

            alert(t('saveSuccess') || '保存しました');
            // Refresh data
            await fetchTalentDetails();
        } catch (e) {
            setError(e.message || t('saveFailed'));
            console.error('[AdminTalentDetailModal] Save residence error:', e);
        } finally {
            setIsLoading(false);
        }
    };


    const handleSaveTalent = async () => {
        setIsLoading(true);
        setError('');
        try {
            // Clean data similar to SalesResidenceInfoModal
            const cleanedData = { ...talentFormData };

            // Remove navigation properties
            delete cleanedData.salesStaff;
            delete cleanedData.residencePermit;
            delete cleanedData.loan;
            delete cleanedData.attachments;
            delete cleanedData.employmentPauses;
            delete cleanedData.clientOrganization;
            delete cleanedData.clientWorkplace;

            // Handle date fields
            if (!cleanedData.dateOfBirth || cleanedData.dateOfBirth === '') {
                cleanedData.dateOfBirth = null;
            }
            if (!cleanedData.contractEndDate || cleanedData.contractEndDate === '') {
                cleanedData.contractEndDate = null;
            }

            // Ensure numeric fields are numbers
            cleanedData.initialCostNotCharged = Boolean(cleanedData.initialCostNotCharged);
            cleanedData.managementFeeNotCharged = Boolean(cleanedData.managementFeeNotCharged);

            if (cleanedData.initialCostNotCharged || cleanedData.initialCost === '' || cleanedData.initialCost === null || cleanedData.initialCost === undefined) {
                cleanedData.initialCost = null;
            } else {
                cleanedData.initialCost = parseFloat(cleanedData.initialCost);
                if (Number.isNaN(cleanedData.initialCost)) {
                    cleanedData.initialCost = null;
                }
            }
            if (cleanedData.introductionFee === '' || cleanedData.introductionFee === null || cleanedData.introductionFee === undefined) {
                cleanedData.introductionFee = null;
            } else {
                cleanedData.introductionFee = parseFloat(cleanedData.introductionFee);
                if (Number.isNaN(cleanedData.introductionFee)) {
                    cleanedData.introductionFee = null;
                }
            }
            if (cleanedData.managementFeeNotCharged) {
                cleanedData.managementFee = 0;
                cleanedData.firstMonthManagementFee = null;
                cleanedData.includedManagementMonths = null;
                cleanedData.tier1Months = null;
                cleanedData.tier1ManagementFee = null;
                cleanedData.tier2Months = null;
                cleanedData.tier2ManagementFee = null;
                cleanedData.billingPlanType = '通常';
            } else {
                cleanedData.managementFee = parseFloat(cleanedData.managementFee) || 0;
                if (cleanedData.firstMonthManagementFee) {
                    cleanedData.firstMonthManagementFee = parseFloat(cleanedData.firstMonthManagementFee) || 0;
                } else {
                    cleanedData.firstMonthManagementFee = null;
                }
            }

            const parseIntOrNull = (value) => {
                if (value === '' || value === null || value === undefined) return null;
                const parsed = parseInt(value, 10);
                return Number.isNaN(parsed) ? null : parsed;
            };
            const parseDecimalOrNull = (value) => {
                if (value === '' || value === null || value === undefined) return null;
                const parsed = parseFloat(value);
                return Number.isNaN(parsed) ? null : parsed;
            };

            cleanedData.includedManagementMonths = parseIntOrNull(cleanedData.includedManagementMonths);
            cleanedData.tier1Months = parseIntOrNull(cleanedData.tier1Months);
            cleanedData.tier2Months = parseIntOrNull(cleanedData.tier2Months);
            cleanedData.tier1ManagementFee = parseDecimalOrNull(cleanedData.tier1ManagementFee);
            cleanedData.tier2ManagementFee = parseDecimalOrNull(cleanedData.tier2ManagementFee);

            const previousBaseFee = Number(talent?.managementFee ?? 0);
            const nextBaseFee = Number(cleanedData.managementFee ?? 0);
            const hasFeeChangeHistory = feeChanges.length > 0;
            const isBaseFeeChanged = !cleanedData.managementFeeNotCharged
                && !Number.isNaN(previousBaseFee)
                && !Number.isNaN(nextBaseFee)
                && previousBaseFee !== nextBaseFee;
            let forceBaseFeeUpdateWhenFeeChanges = false;

            if (hasFeeChangeHistory && isBaseFeeChanged) {
                if (!baseFeeEditUnlocked) {
                    throw new Error(t('baseFeeLockedError'));
                }
                if (!window.confirm(t('baseFeeSaveConfirm'))) {
                    return;
                }
                forceBaseFeeUpdateWhenFeeChanges = true;
            }

            const normalizedPauseRows = sanitizePauseRows(employmentPauseHistory);
            const invalidPause = normalizedPauseRows.find(row => row.endDate && row.endDate < row.startDate);
            if (invalidPause) {
                throw new Error(t('employmentPauseDateRangeError'));
            }

            // Ensure employmentStatus has value
            if (!cleanedData.employmentStatus || cleanedData.employmentStatus === '') {
                cleanedData.employmentStatus = '在職';
            }
            if (!cleanedData.billingPlanType || cleanedData.billingPlanType === '') {
                cleanedData.billingPlanType = '通常';
            }
            const currentPauseSnapshot = resolveCurrentPauseSnapshot(normalizedPauseRows, cleanedData.employmentStatus);
            cleanedData.employmentPauseType = currentPauseSnapshot.pauseType;
            cleanedData.employmentPauseStartDate = currentPauseSnapshot.startDate;
            cleanedData.employmentPauseEndDate = currentPauseSnapshot.endDate;

            await apiService.updateTalent(talentId, cleanedData, { forceBaseFeeUpdateWhenFeeChanges });
            await apiService.updateTalentEmploymentPauses(talentId, normalizedPauseRows);

            alert(t('saveSuccess') || '保存しました');
            setBaseFeeEditUnlocked(false);
            // Refresh data
            await fetchTalentDetails();
            setBillingChartRefreshKey(prev => prev + 1);
        } catch (e) {
            setError(e.message || t('saveFailed'));
            console.error('[AdminTalentDetailModal] Save error:', e);
        } finally {
            setIsLoading(false);
        }
    };

    if (!isOpen) return null;

    const modalStyle = isDesktopModal
        ? {
            position: 'fixed',
            left: modalPosition ? `${modalPosition.x}px` : '50%',
            top: modalPosition ? `${modalPosition.y}px` : '50%',
            transform: modalPosition ? 'none' : 'translate(-50%, -50%)',
            width: 'min(1200px, calc(100vw - 32px))',
            height: 'min(88vh, 960px)',
            minWidth: '980px',
            minHeight: '680px',
            maxWidth: 'calc(100vw - 16px)',
            maxHeight: 'calc(100vh - 16px)',
            resize: 'both',
            overflow: 'hidden'
        }
        : undefined;

    const renderContent = () => {
        if (isLoading) return <p className="text-center py-8">{t('loading')}...</p>;
        if (error) return <p className="text-center py-8 text-red-500">{error}</p>;
        if (!talent) return <p className="text-center py-8">{t('noTalentDataFound')}</p>;

        const currentResidenceStatus = getCurrentResidenceStatusFromEntries(residenceFormData.residenceStatus, renewalEntries);
        const isSpecifiedSkill2 = isSpecifiedSkill2Status(currentResidenceStatus);
        const canUseFeeChangeHistory = !talentFormData.managementFeeNotCharged
            && !isSpecifiedSkill2
            && (talentFormData.billingPlanType === '通常' || !talentFormData.billingPlanType);
        const baseFeeLockActive = canUseFeeChangeHistory
            && (feeChangeLoading || feeChanges.length > 0)
            && !baseFeeEditUnlocked;

        const tabs = {
            basic: (
                <>
                    <div className="flex">
                        {/* 左侧个人信息 */}
                        <dl className="flex-1">
                            <EditItem label={t('fullName')} name="fullName" value={talentFormData.fullName} onChange={handleTalentChange} />
                            <EditItem label={t('fullNameFurigana')} name="fullNameFurigana" value={talentFormData.fullNameFurigana} onChange={handleTalentChange} />
                            <EditItem
                                label={t('managementNumber')}
                                name="managementNumber"
                                value={talentFormData.managementNumber}
                                onChange={handleTalentChange}
                                inputMode="numeric"
                                pattern="\\d*"
                                maxLength={100}
                            />
                            <EditItem label={t('phoneNumber')} name="phoneNumber" value={talentFormData.phoneNumber} onChange={handleTalentChange} />
                            <EditItem label={t('residenceAddress')} name="address" value={talentFormData.address} onChange={handleTalentChange} />
                            <SelectItem
                                label={t('nationality')}
                                name="nationality"
                                value={talentFormData.nationality}
                                onChange={handleTalentChange}
                                options={nationalityOptions}
                                optionType="nationality"
                                t={t}
                                getTranslatedOption={getTranslatedOption}
                            />
                            <EditItem
                                label={t('birthDate')}
                                name="dateOfBirth"
                                type="date"
                                value={talentFormData.dateOfBirth?.split('T')[0]}
                                onChange={handleTalentChange}
                            />
                            <SelectItem
                                label={t('gender')}
                                name="gender"
                                value={talentFormData.gender}
                                onChange={handleTalentChange}
                                options={genderOptions}
                                optionType="gender"
                                t={t}
                                getTranslatedOption={getTranslatedOption}
                            />
                            <SelectItem
                                label={t('field')}
                                name="field"
                                value={showCustomField ? '他' : talentFormData.field}
                                onChange={handleTalentChange}
                                onInput={handleTalentChange}
                                options={fieldOptions}
                                optionType="field"
                                t={t}
                                getTranslatedOption={getTranslatedOption}
                            />
                            {showCustomField && (
                                <EditItem
                                    label={t('customField')}
                                    name="customField"
                                    value={customFieldValue}
                                    onChange={handleCustomFieldChange}
                                />
                            )}
                            <SelectItem
                                label={t('employmentStatus')}
                                name="employmentStatus"
                                value={talentFormData.employmentStatus}
                                onChange={handleTalentChange}
                                options={employmentStatusOptions}
                                optionType="employmentStatus"
                                t={t}
                                getTranslatedOption={getTranslatedOption}
                                required
                            />
                            <div className="grid grid-cols-3 gap-4 py-2 border-b border-gray-100">
                                <div className="text-sm font-medium text-gray-700">{t('employmentPauseType')}履歴</div>
                                <div className="col-span-2">
                                    <div className="flex justify-end mb-2">
                                        <button
                                            type="button"
                                            onClick={handleAddPauseRow}
                                            className="px-3 py-1 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700"
                                        >
                                            + 追加
                                        </button>
                                    </div>
                                    {employmentPauseHistory.length === 0 ? (
                                        <p className="text-xs text-gray-500">{t('none') || 'なし'}</p>
                                    ) : (
                                        <div className="space-y-2">
                                            {employmentPauseHistory.map((row) => (
                                                <div key={row.id} className="grid grid-cols-4 gap-2 items-center">
                                                    <select
                                                        value={row.pauseType || '育休'}
                                                        onChange={(e) => handlePauseRowChange(row.id, 'pauseType', e.target.value)}
                                                        className="border-gray-300 rounded-md shadow-sm py-2 px-2 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                                                    >
                                                        {pauseTypeOptions.map(option => (
                                                            <option key={option} value={option}>
                                                                {getTranslatedOption(option, 'employmentPauseType')}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    <input
                                                        type="date"
                                                        min="1900-01-01"
                                                        max="2099-12-31"
                                                        value={normalizePauseDate(row.startDate)}
                                                        onChange={(e) => handlePauseRowChange(row.id, 'startDate', e.target.value)}
                                                        className="border-gray-300 rounded-md shadow-sm py-2 px-2 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                                                    />
                                                    <input
                                                        type="date"
                                                        min="1900-01-01"
                                                        max="2099-12-31"
                                                        value={normalizePauseDate(row.endDate)}
                                                        onChange={(e) => handlePauseRowChange(row.id, 'endDate', e.target.value)}
                                                        className="border-gray-300 rounded-md shadow-sm py-2 px-2 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemovePauseRow(row.id)}
                                                        className="text-xs text-red-600 hover:text-red-800"
                                                    >
                                                        {t('delete') || '削除'}
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    <div className="text-xs text-gray-500 mt-2">{t('employmentPauseHint')}</div>
                                </div>
                            </div>
                            <SectionTitle title={t('clientManagement.title')} />
                            <ClientSelectionFields
                                value={talentFormData}
                                onChange={handleClientSelectionChange}
                                variant="rows3"
                            />
                            <SectionTitle title={t('costs')} />
                            {isSpecifiedSkill2 ? (
                                <>
                                    <div className="grid grid-cols-3 gap-4 py-2 border-b border-indigo-100 bg-indigo-50/60 px-3 rounded-md">
                                        <div className="text-sm font-medium text-indigo-800 flex items-center">特定技能2号</div>
                                        <div className="col-span-2 text-sm text-indigo-700">
                                            初期費用・管理費は統計対象外です。紹介費のみ登録できます。
                                        </div>
                                    </div>
                                    <EditItem
                                        label={t('introductionFee')}
                                        name="introductionFee"
                                        type="number"
                                        value={talentFormData.introductionFee ?? ''}
                                        onChange={handleTalentChange}
                                    />
                                </>
                            ) : (
                                <>
                            <div className="grid grid-cols-3 gap-4 py-2 border-b border-gray-100">
                                <div className="text-sm font-medium text-gray-700 flex items-center">{t('initialCostNotCharged')}</div>
                                <div className="col-span-2">
                                    <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                                        <input
                                            type="checkbox"
                                            name="initialCostNotCharged"
                                            checked={Boolean(talentFormData.initialCostNotCharged)}
                                            onChange={handleTalentChange}
                                            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                        />
                                        <span>{t('noCharge')}</span>
                                    </label>
                                </div>
                            </div>
                            <EditItem
                                label={t('initialCost')}
                                name="initialCost"
                                type="number"
                                value={talentFormData.initialCost ?? ''}
                                onChange={handleTalentChange}
                                disabled={Boolean(talentFormData.initialCostNotCharged)}
                            />
                            <div className="grid grid-cols-3 gap-4 py-2 border-b border-gray-100">
                                <div className="text-sm font-medium text-gray-700 flex items-center">{t('managementFeeNotCharged')}</div>
                                <div className="col-span-2">
                                    <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                                        <input
                                            type="checkbox"
                                            name="managementFeeNotCharged"
                                            checked={Boolean(talentFormData.managementFeeNotCharged)}
                                            onChange={handleTalentChange}
                                            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                        />
                                        <span>{t('noCharge')}</span>
                                    </label>
                                    {talentFormData.managementFeeNotCharged && (
                                        <p className="text-xs text-gray-500 mt-1">{t('managementFeeNotChargedHint')}</p>
                                    )}
                                </div>
                            </div>
                            <SelectItem
                                label={t('billingPlanType')}
                                name="billingPlanType"
                                value={talentFormData.billingPlanType || '通常'}
                                onChange={handleTalentChange}
                                options={billingPlanTypeOptions}
                                optionType="billingPlanType"
                                t={t}
                                getTranslatedOption={getTranslatedOption}
                                disabled={Boolean(talentFormData.managementFeeNotCharged)}
                            />

                            {!talentFormData.managementFeeNotCharged && (talentFormData.billingPlanType === '通常' || talentFormData.billingPlanType === '初期費+Nヶ月管理費' || !talentFormData.billingPlanType) && (
                                <>
                                    <EditItem
                                        label={t('managementFee')}
                                        name="managementFee"
                                        type="number"
                                        value={talentFormData.managementFee}
                                        onChange={handleTalentChange}
                                        disabled={baseFeeLockActive}
                                    />
                                    {baseFeeLockActive && (
                                        <div className="grid grid-cols-3 gap-4 py-2 border-b border-gray-100">
                                            <div />
                                            <div className="col-span-2 space-y-2">
                                                <p className="text-xs text-amber-700">{t('baseFeeLockHint')}</p>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        if (!window.confirm(t('baseFeeUnlockConfirm'))) {
                                                            return;
                                                        }
                                                        setBaseFeeEditUnlocked(true);
                                                    }}
                                                    className="text-xs px-3 py-1.5 bg-amber-50 text-amber-700 rounded hover:bg-amber-100 transition-colors"
                                                >
                                                    {t('baseFeeUnlockButton')}
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}

                            {!talentFormData.managementFeeNotCharged && talentFormData.billingPlanType === '通常' && (
                                <>
                                    <EditItem label={t('firstMonthManagementFee')} name="firstMonthManagementFee" type="number" value={talentFormData.firstMonthManagementFee} onChange={handleTalentChange} />
                                    <p className="text-xs text-gray-500 mt-1">{t('firstMonthManagementFeeHint')}</p>
                                </>
                            )}

                            {!talentFormData.managementFeeNotCharged && talentFormData.billingPlanType === '初期費+Nヶ月管理費' && (
                                <EditItem label={t('includedManagementMonths')} name="includedManagementMonths" type="number" value={talentFormData.includedManagementMonths ?? ''} onChange={handleTalentChange} />
                            )}

                            {!talentFormData.managementFeeNotCharged && talentFormData.billingPlanType === '段階管理費' && (
                                <>
                                    <EditItem label={t('tier1Months')} name="tier1Months" type="number" value={talentFormData.tier1Months ?? ''} onChange={handleTalentChange} />
                                    <EditItem label={t('tier1ManagementFee')} name="tier1ManagementFee" type="number" value={talentFormData.tier1ManagementFee ?? ''} onChange={handleTalentChange} />
                                    <EditItem label={t('tier2Months')} name="tier2Months" type="number" value={talentFormData.tier2Months ?? ''} onChange={handleTalentChange} />
                                    <EditItem label={t('tier2ManagementFee')} name="tier2ManagementFee" type="number" value={talentFormData.tier2ManagementFee ?? ''} onChange={handleTalentChange} />
                                </>
                            )}

                            {/* Fee Change History - only for 通常 plan */}
                            {!talentFormData.managementFeeNotCharged && (talentFormData.billingPlanType === '通常' || !talentFormData.billingPlanType) && talentId && (
                                <div className="py-3 border-b border-gray-100">
                                    <div className="flex items-center justify-between mb-2">
                                        <h4 className="text-sm font-medium text-gray-700">{t('feeChangeHistory')}</h4>
                                        <button
                                            type="button"
                                            onClick={() => setFeeChangeFormOpen(!feeChangeFormOpen)}
                                            className="text-xs px-2 py-1 bg-indigo-50 text-indigo-600 rounded hover:bg-indigo-100 transition-colors"
                                        >
                                            {feeChangeFormOpen ? t('cancel') : `+ ${t('feeChangeAdd')}`}
                                        </button>
                                    </div>

                                    {/* Add Form */}
                                    {feeChangeFormOpen && (
                                        <div className="mb-3 p-3 bg-gray-50 rounded-lg space-y-2">
                                            <div className="grid grid-cols-3 gap-2">
                                                <div>
                                                    <label className="text-xs text-gray-500">{t('feeChangeEffectiveMonth')}</label>
                                                    <input
                                                        type="month"
                                                        value={feeChangeForm.effectiveMonth}
                                                        onChange={e => setFeeChangeForm(f => ({ ...f, effectiveMonth: e.target.value }))}
                                                        className="block w-full border-gray-300 rounded-md shadow-sm py-1.5 px-2 text-sm focus:ring-indigo-500 focus:border-indigo-500"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="text-xs text-gray-500">{t('feeChangeNewFee')}</label>
                                                    <input
                                                        type="number"
                                                        value={feeChangeForm.newFee}
                                                        onChange={e => setFeeChangeForm(f => ({ ...f, newFee: e.target.value }))}
                                                        className="block w-full border-gray-300 rounded-md shadow-sm py-1.5 px-2 text-sm focus:ring-indigo-500 focus:border-indigo-500"
                                                        min="0"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="text-xs text-gray-500">{t('feeChangeNote')}</label>
                                                    <input
                                                        type="text"
                                                        value={feeChangeForm.note}
                                                        onChange={e => setFeeChangeForm(f => ({ ...f, note: e.target.value }))}
                                                        className="block w-full border-gray-300 rounded-md shadow-sm py-1.5 px-2 text-sm focus:ring-indigo-500 focus:border-indigo-500"
                                                    />
                                                </div>
                                            </div>
                                            <div className="flex justify-end">
                                                <button
                                                    type="button"
                                                    disabled={feeChangeSubmitting || !feeChangeForm.effectiveMonth || !feeChangeForm.newFee}
                                                    onClick={async () => {
                                                        setFeeChangeSubmitting(true);
                                                        try {
                                                            await apiService.addFeeChange(talentId, {
                                                                effectiveMonth: feeChangeForm.effectiveMonth,
                                                                newFee: parseFloat(feeChangeForm.newFee),
                                                                note: feeChangeForm.note || null
                                                            });
                                                            // Refresh fee changes
                                                            const res = await apiService.getFeeChanges(talentId);
                                                            setFeeChanges(res.changes || []);
                                                            setFeeChangeForm({ effectiveMonth: '', newFee: '', note: '' });
                                                            setFeeChangeFormOpen(false);
                                                            setBaseFeeEditUnlocked(false);
                                                            setBillingChartRefreshKey(k => k + 1);
                                                        } catch (err) {
                                                            alert(err.message || t('feeChangeAddFailed'));
                                                        } finally {
                                                            setFeeChangeSubmitting(false);
                                                        }
                                                    }}
                                                    className="text-xs px-3 py-1.5 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                                                >
                                                    {feeChangeSubmitting ? t('feeChangeAdding') : t('feeChangeAddButton')}
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    {/* Fee Change List */}
                                    {feeChangeLoading ? (
                                        <p className="text-xs text-gray-400">{t('loading')}</p>
                                    ) : feeChanges.length === 0 ? (
                                        <p className="text-xs text-gray-400">{t('feeChangeNoRecords')}</p>
                                    ) : (
                                        <div className="overflow-x-auto">
                                            <table className="w-full text-xs">
                                                <thead>
                                                    <tr className="text-gray-500 border-b">
                                                        <th className="text-left py-1 pr-2">{t('feeChangeEffectiveMonth')}</th>
                                                        <th className="text-right py-1 pr-2">{t('feeChangeNewFee')}</th>
                                                        <th className="text-left py-1 pr-2">{t('feeChangeNote')}</th>
                                                        <th className="text-right py-1">{t('actions')}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {feeChanges.map(fc => (
                                                        <tr key={fc.id} className="border-b border-gray-50 hover:bg-gray-50">
                                                            <td className="py-1 pr-2">{fc.effectiveDate}</td>
                                                            <td className="py-1 pr-2 text-right font-medium">{Number(fc.newFee).toLocaleString()}</td>
                                                            <td className="py-1 pr-2 text-gray-500">{fc.note || '-'}</td>
                                                            <td className="py-1 text-right">
                                                                <button
                                                                    type="button"
                                                                    onClick={async () => {
                                                                        if (!window.confirm(t('feeChangeDeleteConfirm'))) return;
                                                                        try {
                                                                            await apiService.deleteFeeChange(talentId, fc.id);
                                                                            const res = await apiService.getFeeChanges(talentId);
                                                                            setFeeChanges(res.changes || []);
                                                                            setBaseFeeEditUnlocked(false);
                                                                            setBillingChartRefreshKey(k => k + 1);
                                                                        } catch (err) {
                                                                            alert(err.message || t('feeChangeDeleteFailed'));
                                                                        }
                                                                    }}
                                                                    className="text-red-500 hover:text-red-700 transition-colors"
                                                                >
                                                                    {t('feeChangeDelete')}
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            )}
                                <SectionTitle title={t('billingTrend')} />
                                <TalentBillingChart talentId={talentId} refreshKey={billingChartRefreshKey} />
                                </>
                            )}

                            <EditItem label={t('contractEndDate')} name="contractEndDate" type="date" value={talentFormData.contractEndDate?.split('T')[0]} onChange={handleTalentChange} />
                        </dl>

                        {/* 右侧照片区域 - 与氏名字段对齐 */}
                        <div className="ml-6 flex flex-col items-center space-y-2">
                            {/* 写真プレビューエリア - 3cm x 4cm */}
                            <div
                                className="border-2 border-dashed border-gray-300 rounded-lg bg-gray-50 flex items-center justify-center overflow-hidden"
                                style={{ width: '113px', height: '151px' }}
                            >
                                {photoPreviewUrl ? (
                                    <img
                                        src={photoPreviewUrl}
                                        alt="人材写真"
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    <div className="text-center p-2">
                                        <svg className="w-8 h-8 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                        </svg>
                                        <p className="text-xs text-gray-500">{t('noPhoto') || '写真なし'}</p>
                                    </div>
                                )}
                            </div>

                            {/* Size information */}
                            <p className="text-xs text-gray-600">3cm × 4cm</p>

                            {/* Photo upload and download buttons */}
                            <div className="flex space-x-2">
                                <label className="cursor-pointer">
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={handlePhotoChange}
                                        className="hidden"
                                    />
                                    <span className="text-xs bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700 transition-colors">
                                        アップロード
                                    </span>
                                </label>

                                {existingPhoto && (
                                    <>
                                        <button
                                            type="button"
                                            onClick={() => attachmentPreview.openPreview(existingPhoto.id, existingPhoto.fileName || 'photo.jpg', existingPhoto.fileType)}
                                            className="text-xs bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700 transition-colors"
                                        >
                                            {t('viewOnline')}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleDownloadPhoto}
                                            className="text-xs bg-green-600 text-white px-3 py-1.5 rounded hover:bg-green-700 transition-colors"
                                        >
                                            ダウンロード
                                        </button>
                                    </>
                                )}
                            </div>

                            {(photoPreviewUrl || existingPhoto) && (
                                <button
                                    type="button"
                                    onClick={handleDeletePhoto}
                                    className="text-xs text-red-600 hover:text-red-800"
                                >
                                    {t('delete') || '削除'}
                                </button>
                            )}
                        </div>
                    </div>

                    <SectionTitle title={`${t('attachments')} (Sales)`} />
                    <div className="mt-4">
                        <ul>
                            {allAttachments.filter(att => att.sourceModule === "Sales").map(att => (
                                <li key={att.id} className="flex items-center justify-between py-2 border-b">
                                    <div className="flex items-center">
                                        {isImage(att.fileType) && thumbnailUrls[att.id] ? (
                                            <img src={thumbnailUrls[att.id]} alt={att.fileName} className="w-16 h-16 object-cover mr-4 rounded" />
                                        ) : (
                                            <span className="text-4xl mr-4">📄</span> // Generic file icon, replace with actual icon if desired
                                        )}
                                        <span>{att.fileName}</span>
                                    </div>
                                    <div className="flex items-center gap-4">
                                        <button onClick={() => attachmentPreview.openPreview(att.id, att.fileName, att.fileType)} className="text-blue-500 hover:underline">{t('viewOnline')}</button>
                                        <button onClick={() => handleDownload(att.id, att.fileName)} className="text-blue-500 hover:underline">{t('download')}</button>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                </>
            ),
            residence: (
                <>
                    <dl>
                        <EditItem
                            label={t('interviewDate')}
                            name="interviewDate"
                            type="date"
                            value={residenceFormData.interviewDate?.split('T')[0]}
                            onChange={handleResidenceChange}
                        />
                        <EditItem
                            label={t('applicationDate')}
                            name="applicationDate"
                            type="date"
                            value={residenceFormData.applicationDate?.split('T')[0]}
                            onChange={handleResidenceChange}
                        />
                        <EditItem
                            label={t('grantDate')}
                            name="grantDate"
                            type="date"
                            value={residenceFormData.grantDate?.split('T')[0]}
                            onChange={handleResidenceChange}
                        />
                        <EditItem label={t('applicationMethod')} name="applicationMethod" value={residenceFormData.applicationMethod} onChange={handleResidenceChange} />
                        <EditItem label={t('applicationNumber')} name="applicationNumber" value={residenceFormData.applicationNumber} onChange={handleResidenceChange} />
                        <SelectItem
                            label="在留資格"
                            name="residenceStatus"
                            value={residenceFormData.residenceStatus || ''}
                            onChange={handleResidenceChange}
                            options={RESIDENCE_STATUS_OPTIONS}
                            t={t}
                        />
                        <SelectItem
                            label="申請"
                            name="applicationType"
                            value={residenceFormData.applicationType || ''}
                            onChange={handleResidenceChange}
                            options={renewalApplicationTypeOptions}
                            t={t}
                        />
                        <EditItem label={t('applicant')} name="applicant" value={residenceFormData.applicant} onChange={handleResidenceChange} />
                        <EditItem label={t('residenceCardNumber')} name="residenceCardNumber" value={residenceFormData.residenceCardNumber} onChange={handleResidenceChange} />
                        <EditItem
                            label={t('residenceCardExpiry')}
                            name="residenceCardExpiry"
                            type="date"
                            value={residenceFormData.residenceCardExpiry?.split('T')[0]}
                            onChange={handleResidenceChange}
                        />
                        <EditItem label={t('passportNumber')} name="passportNumber" value={residenceFormData.passportNumber} onChange={handleResidenceChange} />
                        <EditItem
                            label={t('passportExpiry')}
                            name="passportExpiry"
                            type="date"
                            value={residenceFormData.passportExpiry?.split('T')[0]}
                            onChange={handleResidenceChange}
                        />
                        <EditItem
                            label={t('startDate')}
                            name="startDate"
                            type="date"
                            value={residenceFormData.startDate?.split('T')[0]}
                            onChange={handleResidenceChange}
                        />
                        <EditItem
                            label={t('endDate')}
                            name="endDate"
                            type="date"
                            value={residenceFormData.endDate?.split('T')[0]}
                            onChange={handleResidenceChange}
                        />
                        <DetailItem label={t('remainingDays')} value={talent.residencePermit?.remainingDays} />
                    </dl>

                    {/* Renewal Entries Section */}
                    <div className="mt-6 pt-4 border-t border-gray-200">
                        <div className="flex justify-between items-center mb-4">
                            <h4 className="text-sm font-semibold text-gray-700">更新履歴</h4>
                            <button
                                type="button"
                                onClick={handleAddRenewal}
                                className="px-3 py-1 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700"
                            >
                                + 更新履歴を追加
                            </button>
                        </div>
                        {renewalEntries.map((entry, index) => (
                            <div key={entry.id} className="border border-gray-200 rounded-md p-4 mb-3 bg-gray-50 relative">
                                <button
                                    type="button"
                                    onClick={() => handleRemoveRenewal(entry.id)}
                                    className="absolute top-2 right-2 text-red-600 hover:text-red-800 text-sm font-medium"
                                >
                                    削除
                                </button>
                                <EditItem
                                    label="更新日"
                                    name={`renewalDate_${index}`}
                                    type="date"
                                    value={entry.renewalDate?.split('T')[0] || ''}
                                    onChange={(e) => handleRenewalChange(entry.id, 'renewalDate', e.target.value)}
                                />
                                <EditItem
                                    label="交付日"
                                    name={`renewalGrantDate_${index}`}
                                    type="date"
                                    value={entry.renewalGrantDate?.split('T')[0] || ''}
                                    onChange={(e) => handleRenewalChange(entry.id, 'renewalGrantDate', e.target.value)}
                                />
                                <EditItem
                                    label="申請番号"
                                    name={`renewalNumber_${index}`}
                                    value={entry.renewalNumber || ''}
                                    onChange={(e) => handleRenewalChange(entry.id, 'renewalNumber', e.target.value)}
                                />
                                <SelectItem
                                    label="在留資格"
                                    name={`residenceStatus_${index}`}
                                    value={entry.residenceStatus || ''}
                                    onChange={(e) => handleRenewalChange(entry.id, 'residenceStatus', e.target.value)}
                                    options={RESIDENCE_STATUS_OPTIONS}
                                    t={t}
                                />
                                <SelectItem
                                    label="申請"
                                    name={`renewalApplicationType_${index}`}
                                    value={entry.renewalApplicationType || ''}
                                    onChange={(e) => handleRenewalChange(entry.id, 'renewalApplicationType', e.target.value)}
                                    options={renewalApplicationTypeOptions}
                                    t={t}
                                />
                                <SelectItem
                                    label="提出方法"
                                    name={`submissionMethod_${index}`}
                                    value={entry.submissionMethod || ''}
                                    onChange={(e) => handleRenewalChange(entry.id, 'submissionMethod', e.target.value)}
                                    options={renewalSubmissionMethodOptions}
                                    t={t}
                                />
                                <SelectItem
                                    label="提出(取次者)"
                                    name={`submissionAgent_${index}`}
                                    value={entry.submissionAgentOption || getSubmissionAgentOptionValue(entry.submissionAgent)}
                                    onChange={(e) => handleSubmissionAgentOptionChange(entry.id, e.target.value)}
                                    options={renewalSubmissionAgentOptions}
                                    t={t}
                                />
                                {(entry.submissionAgentOption || getSubmissionAgentOptionValue(entry.submissionAgent)) === '他' && (
                                    <EditItem
                                        label="提出(取次者) 他"
                                        name={`submissionAgentOther_${index}`}
                                        value={entry.submissionAgentOther || ''}
                                        onChange={(e) => handleSubmissionAgentOtherChange(entry.id, e.target.value)}
                                    />
                                )}
                            </div>
                        ))}
                        {renewalEntries.length === 0 && (
                            <p className="text-sm text-gray-500 text-center py-4">更新履歴はありません</p>
                        )}
                    </div>

                    <SectionTitle title={`${t('attachments')} (Residence)`} />
                    <div className="mt-4">
                        <ul>
                            {allAttachments.filter(att => att.sourceModule === "Residence").map(att => (
                                <li key={att.id} className="flex items-center justify-between py-2 border-b">
                                    <div className="flex items-center">
                                        {isImage(att.fileType) && thumbnailUrls[att.id] ? (
                                            <img src={thumbnailUrls[att.id]} alt={att.fileName} className="w-16 h-16 object-cover mr-4 rounded" />
                                        ) : (
                                            <span className="text-4xl mr-4">📄</span>
                                        )}
                                        <span>{att.fileName}</span>
                                    </div>
                                    <div className="flex items-center gap-4">
                                        <button onClick={() => attachmentPreview.openPreview(att.id, att.fileName, att.fileType)} className="text-blue-500 hover:underline">{t('viewOnline')}</button>
                                        <button onClick={() => handleDownload(att.id, att.fileName)} className="text-blue-500 hover:underline">{t('download')}</button>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                </>
            ),
            loan: (
                <>
                    <dl>
                        <DetailItem label={t('totalAmount')} value={talent.loan?.totalAmount} />
                        <DetailItem label={t('loanDate')} value={talent.loan?.loanDate} />
                        <DetailItem label={t('repaymentStartDate')} value={talent.loan?.repaymentStartDate} />
                        <DetailItem label={t('repaymentEndDate')} value={talent.loan?.repaymentEndDate} />
                        <DetailItem label={t('remainingBalance')} value={talent.loan?.remainingBalance} />
                    </dl>

                    <div className="pt-4 mt-4 border-t">
                        <dt className="font-medium text-gray-800">{t('loanEntries')}:</dt>
                        {talent.loan?.loanEntries && talent.loan.loanEntries.length > 0 ? (
                            <ul className="list-disc pl-5 mt-2 space-y-1">
                                {talent.loan.loanEntries.map(entry => (
                                    <li key={entry.id} className="text-sm text-gray-700">
                                        <div className="font-semibold">{entry.amount}円</div>
                                        <div className="text-gray-600">{t('loanDate')}: {entry.loanDate}</div>
                                        <div className="text-gray-600">{t('plannedRepaymentEndDate')}: {entry.plannedEndDate}</div>
                                        {entry.note && <div className="text-gray-500">{entry.note}</div>}
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <p className="text-sm text-gray-500 mt-2">{t('none') || 'N/A'}</p>
                        )}
                    </div>

                    <div className="pt-4 mt-4 border-t">
                        <dt className="font-medium text-gray-800">{t('repaymentHistory')}:</dt>
                        {(talent.loan?.repayments && talent.loan.repayments.length > 0) ? (
                            <ul className="list-disc pl-5 mt-2">
                                {talent.loan.repayments.map(r => (
                                    <li key={r.id} className="text-sm">{r.repaymentDate}: {r.amount}円</li>
                                ))}
                            </ul>
                        ) : <p className="text-sm text-gray-500">{t('none') || 'N/A'}</p>}
                    </div>
                </>
            )
        };

        return tabs[activeTab];
    };

    return (
        <>
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
            <div
                ref={modalRef}
                style={modalStyle}
                className={`bg-white rounded-lg shadow-xl flex flex-col ${isDesktopModal ? '' : 'w-full max-w-2xl max-h-[90vh]'}`}
            >
                <div
                    className={`p-6 border-b flex items-center justify-between ${isDesktopModal ? 'cursor-move select-none' : ''}`}
                    onMouseDown={handleModalDragStart}
                >
                    <h2 className="text-2xl font-bold text-gray-800">{talent?.fullName || t('talentDetails')}</h2>
                    {isDesktopModal && (
                        <span className="text-xs text-gray-500">タイトルバーで移動・右下でサイズ変更</span>
                    )}
                </div>
                <div className="p-6 overflow-y-auto flex-1 min-h-0">
                    <div className="border-b border-gray-200 mb-4">
                        <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                            <button onClick={() => setActiveTab('basic')} className={`${activeTab === 'basic' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}>{t('personalInfo')}</button>
                            <button onClick={() => setActiveTab('residence')} className={`${activeTab === 'residence' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}>{t('residenceInfo')}</button>
                            <button onClick={() => setActiveTab('loan')} className={`${activeTab === 'loan' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}>{t('loanInfo')}</button>
                        </nav>
                    </div>
                    {renderContent()}
                </div>
                {error && <p className="px-6 pb-4 text-sm text-red-600">{error}</p>}
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                    <button
                        type="button"
                        onClick={() => {
                            if (activeTab === 'basic') {
                                handleSaveTalent();
                            } else if (activeTab === 'residence') {
                                handleSaveResidence();
                            }
                        }}
                        disabled={isLoading}
                        className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm disabled:bg-indigo-400"
                    >
                        {isLoading ? t('saving') : t('save')}
                    </button>
                    <button
                        type="button"
                        onClick={onClose}
                        className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm"
                    >
                        {t('close')}
                    </button>
                </div>
            </div>
        </div>
        <AttachmentPreviewModal
            previewState={attachmentPreview.previewState}
            onClose={attachmentPreview.closePreview}
        />
        </>
    );
}
